import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, Users as UsersIcon, Crown } from "lucide-react";
import AppHeader from "@/components/layout/AppHeader";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fullName(u) {
  const parts = [u.nombre_pila, u.apellido_paterno, u.apellido_materno].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return u.name || "(sin nombre)";
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

function RoleBadge({ role }) {
  const map = {
    owner:  { bg: "bg-[#1E3A5F]", fg: "text-white",   label: "Propietario" },
    content_editor: { bg: "bg-[#EDF5F3]", fg: "text-[#1F4640]", label: "Editor de contenido" },
    asesor: { bg: "bg-slate-100", fg: "text-slate-700", label: "Asesor" },
  };
  const s = map[role] || map.asesor;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
      {role === "owner" && <Crown className="w-3 h-3" />} {s.label}
    </span>
  );
}

export default function AdminUsersPage() {
  const nav = useNavigate();
  const { user, loading } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [pending, setPending] = useState(null); // id en flight
  const [scope, setScope] = useState("cuenta");
  const all = scope === "plataforma";

  const isOwner = user?.role === "owner";

  useEffect(() => {
    // Solo owner/admin pueden ver; solo owner puede modificar.
    if (!loading && user && user.role !== "owner") {
      nav("/app", { replace: true });
    }
  }, [loading, user, nav]);

  useEffect(() => {
    let alive = true;
    setLoadingList(true);
    (async () => {
      try {
        const { data } = await api.get(scope === "plataforma" ? "/admin/platform-users" : "/admin/users");
        if (alive) setUsers(data);
      } catch (e) {
        toast.error(e.response?.data?.detail || "No se pudo cargar la lista");
      } finally {
        if (alive) setLoadingList(false);
      }
    })();
    return () => { alive = false; };
  }, [scope]);

  const counts = useMemo(() => {
    const total = users.length;
    const admins = users.filter((u) => u.role === "content_editor").length;
    const asesores = users.filter((u) => u.role === "asesor").length;
    return { total, admins, asesores };
  }, [users]);

  const setRole = async (u, role, puede_aprobar) => {
    if (!isOwner || u.role === "owner") return;
    setPending(u.id);
    try {
      const { data } = await api.post(`/admin/${all ? "platform-users" : "users"}/${u.id}/role`, { role, puede_aprobar });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, ...data } : x)));
      toast.success(`Permisos de ${fullName(data)} actualizados`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al cambiar el rol");
    } finally {
      setPending(null);
    }
  };

  return (
    <div className="min-h-screen">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="admin-usuarios-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EDF5F3] text-[#1F4640] text-xs font-bold uppercase tracking-widest">
              <ShieldCheck className="w-3.5 h-3.5" /> Panel Propietario
            </div>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl font-extrabold text-slate-900" data-testid="admin-usuarios-title">
              Usuarios y permisos
            </h1>
            <p className="mt-2 text-slate-600 max-w-2xl">
              Un editor de contenido solo entra al panel del Copiloto y FAQ: no ve operaciones, personas ni documentos.
              Con «Puede aprobar» además marca entradas como aprobadas legalmente; publicar siempre es tuyo.
              El usuario debe haber iniciado sesión por SMS al menos una vez para aparecer aquí.
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-slate-500 uppercase font-semibold tracking-widest">Resumen</div>
            <div className="flex items-center gap-2 mt-1 flex-wrap justify-end">
              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-full px-2 py-1">
                <UsersIcon className="w-3.5 h-3.5" /> {counts.total} usuarios
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-[#1F4640] bg-[#EDF5F3] rounded-full px-2 py-1">
                {counts.admins} editores
              </span>
              <span className="inline-flex items-center gap-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-full px-2 py-1">
                {counts.asesores} asesor
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 inline-flex rounded-full bg-[#F3EFE6] p-1" data-testid="usuarios-scope">
          {[["cuenta", "Mi cuenta"], ["plataforma", "Todos los usuarios"]].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setScope(k)} data-testid={`usuarios-scope-${k}`}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${scope === k ? "bg-white text-slate-900 shadow-sm" : "text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>
        {all && (
          <p className="mt-3 text-sm text-slate-600 max-w-3xl" data-testid="usuarios-plataforma-nota">
            Cada persona que se registra por SMS recibe su propia cuenta. Si la haces Editor de contenido, trabaja sobre el contenido general
            del Copiloto y FAQ, y deja de poder usar operaciones, personas y documentos de su cuenta hasta que la regreses a Asesor.
          </p>
        )}

        {!isOwner && (
          <div className="mt-6 rounded-xl border border-[#E2DCD2] bg-[#FFF7EE] px-4 py-3 text-sm text-[#92400E]" data-testid="admin-usuarios-readonly-banner">
            Estás viendo esta pantalla en modo solo-lectura. Para cambiar roles pide al propietario de la cuenta.
          </div>
        )}

        <div className="mt-6 rounded-2xl bg-white border border-[#E2DCD2] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-usuarios-table">
            <thead className="bg-[#F3EFE6] text-slate-700 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Usuario</th>
                {all && <th className="text-left px-4 py-3 w-44">Cuenta</th>}
                <th className="text-left px-4 py-3 w-40">Teléfono</th>
                <th className="text-left px-4 py-3 w-52">Rol</th>
                <th className="text-left px-4 py-3 w-40">Último acceso</th>
                <th className="text-right px-4 py-3 w-40">Puede aprobar</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2DCD2]">
              {loadingList && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>
              )}
              {!loadingList && users.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Aún no hay otros usuarios en tu cuenta.</td></tr>
              )}
              {users.map((u) => {
                const self = u.id === user?.id;
                const canToggle = isOwner && u.role !== "owner" && !self;
                return (
                  <tr key={u.id} className="hover:bg-[#FBF9F5] align-top" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{fullName(u)}</div>
                      {self && <div className="text-[10px] text-slate-400 mt-0.5">(tú)</div>}
                    </td>
                    {all && (
                      <td className="px-4 py-3 text-slate-700" data-testid={`user-account-${u.id}`}>
                        {u.account_id === user?.account_id ? "Tu cuenta" : u.account_type === "independent" ? "Cuenta propia" : (u.account_name || "—")}
                      </td>
                    )}
                    <td className="px-4 py-3 text-slate-700">{u.phone || "—"}</td>
                    <td className="px-4 py-3">
                      {canToggle ? (
                        <Select value={u.role} onValueChange={(r) => setRole(u, r, false)} disabled={pending === u.id}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="asesor">Asesor</SelectItem>
                            <SelectItem value="content_editor">Editor de contenido</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : <RoleBadge role={u.role} />}
                    </td>
                    <td className="px-4 py-3 text-slate-600" data-testid={`user-last-login-${u.id}`}>{fmtDate(u.last_login_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-2">
                        <span className="text-xs text-slate-500" data-testid={`role-label-${u.id}`}>{u.role === "owner" ? "Incluido" : u.role !== "content_editor" ? "—" : u.puede_aprobar ? "Sí" : "No"}</span>
                        <Switch
                          checked={u.role === "owner" || !!u.puede_aprobar}
                          disabled={!canToggle || u.role !== "content_editor" || pending === u.id}
                          onCheckedChange={(v) => setRole(u, "content_editor", v)}
                          data-testid={`role-toggle-${u.id}`}
                          aria-label={`Cambiar rol de ${fullName(u)}`}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
